import { FormikProps } from 'formik'
import React, { ReactElement, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { BookingStatusResponse } from '@api/booking'
import { CreateResponse as ReservationCreateResponse } from '@api/reservation'
import * as ReservationPay from '@api/reservation/pay'
import config from '@config'
import { CheckoutSteps, ReservationErrors } from '@enums'
import useBookingFlow, { ConfirmPaymentParams, FinishPaymentParams } from '@hooks/useBookingFlow'
import amplitude from '@lib/analytics/amplitude'
import { ApiError } from '@lib/api'
import checkoutUtils from '@lib/checkout'
import { CallLoaderOptions } from '@lib/loader'
import { MutationResult } from '@lib/mutation'
import { ShopperError } from '@lib/polling'
import { EventType, postMessage } from '@lib/postMessage'
import reservationUtils from '@lib/reservation'
import { useBookingStatusPolling } from '@loaders/bookingStatusPolling'
import { useReservationCreate } from '@loaders/createReservation'
import { CheckoutFormProps } from '@pages/Checkout/Form'
import ReservationErrorHandler from '@pages/Checkout/Form/SubmitReservation/ReservationErrorHandler'
import { CheckoutFormData } from '@pages/Checkout/hooks/useInitialFormValues'
import useReservationErrors from '@pages/Checkout/hooks/useReservationErrors'
import { useReservationSession } from '@pages/Checkout/hooks/useReservationSession'
import ReservationsExpiredModal from '@pages/Checkout/ReservationsExpiredModal'
import { PluginContext } from '@plugins/index'
import { useSettings } from '@queries/settings'
import { useCheckout } from '@stores/checkout'
import { ParamsStore, useParams } from '@stores/params'

interface SubmitReservationProps<TMutationParams> {
  form: React.FC<CheckoutFormProps>
  submitHook: (
    callOptions?: CallLoaderOptions<TMutationParams | null, ReservationPay.Response>,
  ) => MutationResult<ReservationPay.Response, ApiError, TMutationParams>
  transformParams: (
    data: CheckoutFormData,
    params: ParamsStore,
    outboundConnection: Connection | null,
  ) => TMutationParams
  confirmationOnly?: boolean
}

const SubmitReservation = <T,>(props: SubmitReservationProps<T>): ReactElement | null => {
  const { form: CheckoutForm, submitHook, transformParams } = props
  const [params] = useParams()
  const [{ quickReservation, reselling }] = useSettings()
  const [{ inbound, outbound, step }, setCheckout] = useCheckout()
  const navigate = useNavigate()
  const { callbacks } = useContext(PluginContext)

  const form = useRef<FormikProps<CheckoutFormData>>(null)
  const [stopReservationTimer, setStopReservationTimer] = useState<boolean>(false)
  const [paymentAttempts, setPaymentAttempts] = useState<number>(0)
  const { pathname, search } = useLocation()
  const isMultistep = step !== CheckoutSteps.None

  const finishPayment = useCallback(
    (path: string) => {
      if (form.current && outbound) {
        amplitude.checkout.purchaseTrip({
          connections: { outbound, inbound },
          formData: form.current.values,
          currency: params.currency,
          reselling: reselling.enabled,
        })
      }
      reservationUtils.resetSessionStorage(params)
      navigate(checkoutUtils.buildConfirmationPageUrl(params, path))
    },
    [inbound, navigate, outbound, params, reselling.enabled],
  )
  const bookingStatusProps = useMemo(
    () => ({
      onSuccess: ({ redirectPath }: BookingStatusResponse) => {
        finishPayment(redirectPath)
      },
    }),
    [finishPayment],
  )

  const { mutate: startPolling, ...statusPolling } = useBookingStatusPolling(bookingStatusProps)
  const onSuccess = useCallback(
    (params: FinishPaymentParams) => {
      if (params.action === 'verify_reservation' && params.bookingFormId != null) startPolling(params.bookingFormId)
      if (params.action === 'redirect' && params.redirectPath) finishPayment(params.redirectPath)
    },
    [finishPayment, startPolling],
  )

  const bookingHandlers = useBookingFlow({ form, onSuccess })

  const reservationConfirm = submitHook({
    onSuccess: data => bookingHandlers.confirmPayment(data as ConfirmPaymentParams),
    onError: error => {
      if (error?.message === ReservationErrors.InProgress && form.current?.values.reservationData?.bookingFormId) {
        startPolling(form.current.values.reservationData.bookingFormId)
      }
    },
  })
  const quickReservationCreate = useReservationCreate()
  const isLoading = quickReservationCreate.isLoading || reservationConfirm.isLoading || statusPolling.isLoading

  const confirmReservation = (data: CheckoutFormData): void => {
    setPaymentAttempts(paymentAttempts + 1)
    reservationConfirm.mutate(transformParams(data, params, outbound))
  }

  const processReservationData = (reservation: ReservationCreateResponse): void => {
    form.current?.setFieldValue('reservationData', reservationUtils.adjustTimeZones(reservation))
    reservationUtils.setCheckoutPath(String(reservation.id), `${pathname}${search}`)
  }

  const updatePriceAndFees = (reservation: ReservationCreateResponse): void => {
    form.current?.setFieldValue('price', reservation.price)
    form.current?.setFieldValue('fees', reservation.fees)
  }

  const submitQuickReservation = async (data: CheckoutFormData): Promise<void> => {
    const buildParams: {
      data: CheckoutFormData
      params: ParamsStore
      inbound: Connection | null
      outbound: Connection
    } = { data, params, inbound, outbound: outbound as Connection }
    const createParams = {
      ...reservationUtils.buildQuickCreateParams(buildParams),
      ...(callbacks?.checkout?.getQuickReservationCreateParams?.(data) || {}),
    }
    const reservation = await quickReservationCreate.mutateAsync(createParams)
    if (
      config.sendEventToParentWindow.byRpn.includes(params.retailerPartnerNumber) &&
      (buildParams.data.paymentMethod === 'ox' || buildParams.data.paymentMethod === 'hold_reservation')
    ) {
      postMessage(EventType.RESERVATION_CREATED, reservation.fullResponse, params.parentDomain)
    }
    const shouldConfirm = bookingHandlers.quickReservation(reservation).shouldConfirm && !isMultistep

    if (isMultistep) {
      processReservationData(reservation)
      updatePriceAndFees(reservation)
      setCheckout({ step: step + 1 })
    }

    if (shouldConfirm) {
      processReservationData(reservation)
      confirmReservation({ ...data, reservationData: reservationUtils.adjustTimeZones(reservation) })
    }
  }

  const submit = async (formData: CheckoutFormData): Promise<void> => {
    const isPaymentStep = step === CheckoutSteps.Payment

    if (!isMultistep && formData.checkCardState === false) return
    if (isMultistep && isPaymentStep && formData.checkCardState === false) return

    const data = await bookingHandlers.submitForm(formData)
    const retryConfirmation = statusPolling.error instanceof ShopperError && statusPolling.error.confirmationOnly

    const isQuickReservationAllowed = quickReservation.enabled && !props.confirmationOnly && !retryConfirmation

    if (isQuickReservationAllowed && !isPaymentStep) {
      await submitQuickReservation(data)
    } else {
      confirmReservation(data)
    }
  }

  const { initializeReservation, createError, expiredError, timer } = useReservationSession(form)
  useEffect(() => {
    if (!quickReservation.enabled && !props.confirmationOnly) {
      initializeReservation()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outbound])
  const checkPrice = useCallback(
    ({ reservationData }: CheckoutFormData): boolean =>
      !!reservationData &&
      checkoutUtils.totalPriceWithFees(reservationData.price, reservationData.fees).fractional <= 0,
    [],
  )

  const errors = useMemo(
    () => ({
      reservationCreate: createError,
      quickReservationCreate: quickReservationCreate.error,
      pollingError: statusPolling.error,
      confirmError: reservationConfirm.error,
    }),
    [createError, quickReservationCreate.error, reservationConfirm.error, statusPolling.error],
  )

  const { errorType, errorProp, closeModal } = useReservationErrors({ errors, isLoading, paymentAttempts })

  useEffect(() => {
    if (errorType === 'booking') setStopReservationTimer(true)
  }, [errorType])

  const handleCloseError = (): void => {
    closeModal()
    form.current?.setSubmitting(false)
  }

  return (
    <>
      <CheckoutForm
        finishPayment={onSuccess}
        submit={submit}
        validate={bookingHandlers.validate}
        innerRef={form}
        isLoading={isLoading}
        error={errorProp}
        confirmationOnly={props.confirmationOnly}
        timer={timer && { ...timer, isStopped: stopReservationTimer }}
        isRefund={checkPrice}
      />
      <ReservationErrorHandler type={errorType} onClose={handleCloseError} />
      <ReservationsExpiredModal opened={expiredError != null} period={expiredError?.period} />
    </>
  )
}

export default SubmitReservation
